/**
 * To be used with the `updateQuery` prop of a call to `fetchMore`, where it appends
 * the next results to the previous results.
 * Use case: Infinite scroll.
 *
 * Example:
 * ```
 * this.$apollo.queries.<queryName>.fetchMore({
 *  variables: {
 *     first: 10,
 *     after: this.cursor,
 *   },
 *   updateQuery: appendNextResults,
 * });
 * ```
 */
export const appendNextResults = (
  previousResult,
  { fetchMoreResult }
) => {
  // Find the first key that has an `edges` property.
  const keyWithEdges = Object.keys(previousResult).find(
    (key) => !!previousResult[key].edges
  );
  // Merge the previous and next results.
  return {
    ...previousResult,
    [keyWithEdges]: {
      ...previousResult[keyWithEdges],
      ...fetchMoreResult[keyWithEdges],
      edges: [
        ...previousResult[keyWithEdges].edges,
        ...fetchMoreResult[keyWithEdges].edges,
      ],
    },
  };
};

/**
 * To be used with the `updateQuery` prop of a call to `fetchMore` (of offset pagination queries),
 * where it appends the next results to the previous results.
 * Use case: Infinite scroll.
 *
 * Example:
 * ```
 * this.$apollo.queries.<queryName>.fetchMore({
 *  variables: {
 *     first: 10,
 *     after: this.cursor,
 *   },
 *   updateQuery: appendNextOffsetPageResults({ removeDuplicates: true }),
 * });
 * ```
 */
export const appendNextOffsetPageResults = ({
  removeDuplicates = false,
} = {}) => (previousResult, { fetchMoreResult }) => {
  // Find the first key that has an `nodes` property.
  const keyWithNodes = Object.keys(previousResult).find(
    (key) => !!previousResult[key].nodes
  );

  let previousNodeMap = {};
  if (removeDuplicates) {
    previousNodeMap = previousResult[keyWithNodes].nodes.reduce(
      (acc, node) => {
        acc[node.id] = node;
        return acc;
      },
      {}
    );
  }

  let nextNodes = fetchMoreResult[keyWithNodes].nodes;
  if (removeDuplicates) {
    nextNodes = nextNodes.filter((node) => !previousNodeMap[node.id]);
  }

  // Merge the previous and next results.
  return {
    ...previousResult,
    ...fetchMoreResult,
    highlights: [
      ...(previousResult.highlights || []),
      ...(fetchMoreResult.highlights || []),
    ],
    [keyWithNodes]: {
      ...previousResult[keyWithNodes],
      ...fetchMoreResult[keyWithNodes],
      nodes: [...previousResult[keyWithNodes].nodes, ...nextNodes],
    },
  };
};
